import { SubscriptionTier } from '@/../generated-client/models';
import type { OrganizationOnAzure } from '@/../generated-client/models';

interface PoolCountsShape {
  ops_subscription_count?: number;
  respond_subscription_count?: number;
  ops_seats_used?: number;
  respond_seats_used?: number;
  connect_seats_used?: number;
}

// OrganizationOnAzure only carries the purchased pool sizes at the org level
// (ops_subscription_count / respond_subscription_count) — seats *used* live per
// group as auth-id lists. hasAvailableSeat needs the org-wide used count, so
// sum the per-group lists here rather than relying on a field that doesn't
// exist on the real model.
export function buildPoolCounts(organization: OrganizationOnAzure): PoolCountsShape {
  const groups = Object.values(organization.groups ?? {});
  return {
    ops_subscription_count: organization.ops_subscription_count,
    respond_subscription_count: organization.respond_subscription_count,
    ops_seats_used: groups.reduce((sum, g) => sum + (g?.ops_user_auth_ids?.length ?? 0), 0),
    respond_seats_used: groups.reduce((sum, g) => sum + (g?.respond_user_auth_ids?.length ?? 0), 0),
    connect_seats_used: groups.reduce((sum, g) => sum + (g?.connect_user_auth_ids?.length ?? 0), 0),
  };
}

const clampNonNegative = (n: number) => (n > 0 ? n : 0);

export function getPoolUsage(org: PoolCountsShape, tier: SubscriptionTier): number {
  if (tier === SubscriptionTier.Respond) return org.respond_seats_used ?? 0;
  if (tier === SubscriptionTier.Ops) return org.ops_seats_used ?? 0;
  return 0;
}

export function getPoolPurchased(org: PoolCountsShape, tier: SubscriptionTier): number {
  if (tier === SubscriptionTier.Respond) return org.respond_subscription_count ?? 0;
  if (tier === SubscriptionTier.Ops) return org.ops_subscription_count ?? 0;
  return 0;
}

export function getPoolRemaining(org: PoolCountsShape, tier: SubscriptionTier): number {
  if (tier === SubscriptionTier.Connect) return Number.POSITIVE_INFINITY;
  return clampNonNegative(getPoolPurchased(org, tier) - getPoolUsage(org, tier));
}

// Connect is always available (no seat). Paid tiers need remaining > 0.
export function hasAvailableSeat(org: PoolCountsShape, tier: SubscriptionTier): boolean {
  if (tier === SubscriptionTier.Connect) return true;
  return getPoolRemaining(org, tier) > 0;
}
